/**
 * Slide-to-unlock
 * @module 'modules/slide-unlock'
 *
 * Creates a "slide to unlock" UI component, with a custom unlock action
 *
 * Example HTML structure:
 *
 * <div class="slide-unlock" data-unlocked="false">
 *   <div class="slide-unlock__track">
 *     <div class="slide-unlock__label"></div>
 *     <div class="slide-unlock__thumb"></div>
 *   </div>
 * </div>
 *
 */

/**
 * LIM helper function
 * @param {number} v - The value to be limited
 * @param {number} min - The minimum possible value
 * @param {number} max - The maximum possible value
 *
 * @returns {number} - The limited value
 */
const limit = (v, min, max) => v < min ? min : v > max ? max : v;

class SlideUnlock {
  /**
   * @param {object} [options] -
   * @param {object} [options.selectors] - Element selectors configuration
   * @param {object} [options.containerSelector] - The root/container element
   * @param {object} [options.trackSelector] - The runnable track
   * @param {object} [options.thumbSelector] - The draggable element
   * @param {object} [options.labelSelector] - The label element
   *
   * @param {number} [options.unlockTolerance] - Slider has to be dragged at least this close to the end to unlock. Value should be within 0 and 1, where 0 = no tolerance.
   * @param {function} [options.unlockAction] - A function that gets called when the component is successfully unlocked. This function can refer to `this.currentSlider` to perform actions on the current slider.
   */
  constructor(options) {
    const defaults = {
      containerSelector: '.slide-unlock',
      trackSelector: '.slide-unlock__track',
      thumbSelector: '.slide-unlock__thumb',
      labelSelector: '.slide-unlock__label',
      unlockTolerance: 0.01,
      unlockAction: this.enableUnlockedText.bind(this)
    }

    this.currentSlider = null;

    Object.assign(this, defaults, options);
  }

  /**
   * Initialization creates bound event handlers and iterates through each slide unlock on the page
   */
  init() {
    this.boundHandlers = {
      startDragging: this.startDragging.bind(this),
      stopDragging: this.stopDragging.bind(this),
      drag: this.drag.bind(this)
    };

    this.sliders = Array.prototype.slice.call(
      document.querySelectorAll(
        this.containerSelector
      )
    ).map(this.initSlider, this);

    this.sliders.forEach(this.setInitialState, this);
  }

  /**
   * Unlocks sliders with data-unlocked="true"
   *
   * @param {Object} slider - The slider to check & unlock
   */
  setInitialState(slider) {
    const initiallyUnlocked = slider.container.dataset.unlocked === 'true';
    if(initiallyUnlocked) {
      this.currentSlider = this.sliders.filter(function(s) {
        return s.container === slider.container;
      })[0];
      this.unlock({ initial: true });
    }
  }

  /**
   * Initializes a single slide unlock's elements and events
   *
   * @param {HTMLElement} slider - The root/container element of a slide unlock
   *
   * @returns {object} - reference to the parts of the subject slide unlock
   */
  initSlider(slider) {
    const track = slider.querySelector(this.trackSelector);
    const thumb = slider.querySelector(this.thumbSelector);
    const label = slider.querySelector(this.labelSelector);
    thumb.addEventListener('mousedown', this.boundHandlers.startDragging, { passive: true });
    thumb.addEventListener('touchstart', this.boundHandlers.startDragging, { passive: true });
    track.addEventListener('selectstart', function(event) {
      event.preventDefault();
      return false;
    });

    return {
      container: slider,
      track: track,
      thumb: thumb,
      label: label,
    }
  }

  /**
   * Event handler for when user starts dragging a thumb
   *
   * @param {object} event - `mousedown` or `touchstart` event
   */
  startDragging(event) {
    // Find the cursor X position
    let mouseX = event.clientX;
    if(event.targetTouches && event.targetTouches.length > 0) {
      mouseX = event.targetTouches[0].clientX;
    }

    // find the current slider within `this.sliders` and set a reference
    try {
      this.currentSlider = this.sliders.filter(function(slider) {
        return slider.thumb === event.currentTarget;
      })[0];
    } catch(e) {
      /* could not find subject slider */
      return;
    }

    if (this.isDisabled(this.currentSlider)) return;

    // clear the transition that gets set if slider becomes reset
    this.currentSlider.thumb.style.transition = '';

    // save the initial offset between cursor and the thumb
    this.initialThumbOffset = this.currentSlider.thumb.getBoundingClientRect().left - mouseX;

    // add event handlers depending on the type of the initial event (mouse/touch)
    this.dragEvent = event.type === 'mousedown' ? 'mousemove' : 'touchmove';
    this.stopEvent = event.type === 'mousedown' ? 'mouseup' : 'touchend';
    window.addEventListener(this.dragEvent, this.boundHandlers.drag, {passive: false});
    window.addEventListener(this.stopEvent, this.boundHandlers.stopDragging);
  }

  /**
   * Event handler for when user releases a thumb
   *
   * @param {object} event - `mouseup` or `touchend` event
   */
  stopDragging(event) {
    if (this.isDisabled(this.currentSlider)) return;

    // remove dragging and releasing events
    window.removeEventListener(
      this.dragEvent, this.boundHandlers.drag, {passive: false});
    window.removeEventListener(
      this.stopEvent, this.boundHandlers.stopDragging);

    // check if slider was released at the right edge of the track (minus tolerance)
    if(this.currentUnlockPos >= 1 - this.unlockTolerance) {
      this.unlock();
    } else {
      this.resetThumb();
    }

    // clear reference to current slider
    this.currentSlider = false;
  }

  /**
   * Event handler for when user drags a thumb
   *
   * @param {object} event - `mousemove` or `touchmove` event
   */
  drag(event) {
    if (this.isDisabled(this.currentSlider)) return;

    // prevent label text from becoming selected by the dragging action
    event.preventDefault();

    // find cursor X position
    let mouseX = event.clientX;
    if(event.targetTouches && event.targetTouches.length > 0) {
      mouseX = event.targetTouches[0].clientX;
    }

    // find the position of dragging action relative to the thumb's position within the track
    const thumbRect = this.currentSlider.thumb.getBoundingClientRect();
    const trackRect = this.currentSlider.track.getBoundingClientRect();
    const cursorTrackPos = (mouseX - trackRect.left) / trackRect.width;

    // find the thumb's target position within the track
    const thumbPos = limit(
      (cursorTrackPos * trackRect.width) + this.initialThumbOffset,
      0, trackRect.width - thumbRect.width
    );
    this.currentUnlockPos = thumbPos / (trackRect.width - thumbRect.width);

    this.updateCurrentThumb(thumbPos);
  }

  /**
   * Moves the current slider's thumb to it's unlocked position
   */
  moveThumbToUnlockedPosition() {
    // find the track's runnable area size
    const thumbRect = this.currentSlider.thumb.getBoundingClientRect();
    const trackRect = this.currentSlider.track.getBoundingClientRect();

    // find the thumb's target position within the track
    const thumbPos = trackRect.width - thumbRect.width;
    this.currentUnlockPos = thumbPos / (trackRect.width - thumbRect.width);

    this.updateCurrentThumb(thumbPos);
  }

  /**
   * Applies a transform to the thumb to position it on the track
   *
   * @param {number} thumbPos - Thumb's X-offset in pixels
   */
  updateCurrentThumb(thumbPos) {
    if (this.isDisabled(this.currentSlider)) return;

    this.currentSlider.thumb.style.transform =
      'translateX(' + thumbPos + 'px)';
  }

  /**
   * Disables a slide unlock once it has been successfully unlocked
   */
  unlock({ initial } = {}) {
    if (this.isDisabled(this.currentSlider)) return;

    this.moveThumbToUnlockedPosition();

    this.currentSlider.thumb.removeEventListener(
      'mousedown', this.boundHandlers.startDragging, { passive: true }
    );
    this.currentSlider.thumb.removeEventListener(
      'touchstart', this.boundHandlers.startDragging, { passive: true }
    );

    window.addEventListener('resize', this.moveThumbToUnlockedPosition.bind(this));
    this.currentSlider.container.dataset.unlocked = 'true';

    if(!initial && typeof this.unlockAction === 'function') {
      this.unlockAction(this.currentSlider);
    }
  }

  /**
   * Returns a slider that matches to the given slider container
   *
   * @param {HTMLElement} container The container of the slide unlock
   * @returns {Object} slider object similar to the often used this.currentSlider, or null if not found
   */
  getSliderByContainer(container) {
    try {
      return this.sliders.filter(function(slider) {
        return slider.container === container;
      })[0];
    } catch(e) {
      return null;
    }
  }

  /**
   * Changes the text of the current slider to unlocked text
   *
   * @param {Object} slider - The slider, often this.currentSlider.
   */
  enableUnlockedText(slider) {
    slider.label.innerText = slider.container.dataset.unlockedText;
  }

  /**
   * If a slider is disabled, it can not be unlocked.
   *
   * @param {Object} slider The current slider, typically this.currentSlider
   * @returns {boolean} True if slider is in disabled state, false otherwise
   */
  isDisabled(slider) {
    if (!slider) return false;
    return slider.container.dataset.disabled === 'true' ||
           slider.container.dataset.disabled === true;
  }

  /**
   * Removes possible disabled state from the slider
   *
   * @param {Object} slider The current slider, typically this.currentSlider
   */
  enable(slider) {
    if (!slider) return;
    slider.container.dataset.disabled = false;
  }

  /**
   * Disables the slider
   *
   * @param {Object} slider The current slider, typically this.currentSlider
   */
  disable(slider) {
    slider.dataset.disabled = true;
  }

  /**
   * Resets the thumb's position
   */
  resetThumb() {
    this.currentSlider.thumb.style.transition = 'transform 0.3s ease-out';
    this.currentSlider.thumb.style.transform = 'translateX(0px)';
  }
}

module.exports = SlideUnlock;
